"use client";
import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────

const COLORS = [
  "#F97316", "#3B82F6", "#10B981", "#8B5CF6",
  "#EF4444", "#EC4899", "#14B8A6", "#F59E0B",
];

const CATEGORIES = [
  { id: "action", label: "Action", icon: "⚡" },
  { id: "navigation", label: "Navigation", icon: "🔗" },
  { id: "state", label: "State Change", icon: "🔄" },
  { id: "validation", label: "Validation", icon: "✅" },
  { id: "api", label: "API Call", icon: "📡" },
  { id: "note", label: "Note", icon: "📝" },
];

const NODE_TYPE_ICONS = {
  FRAME: "🖼",
  COMPONENT: "◆",
  COMPONENT_SET: "◆",
  INSTANCE: "◇",
  GROUP: "📁",
  TEXT: "T",
  RECTANGLE: "▬",
  ELLIPSE: "⬭",
  VECTOR: "✦",
  BOOLEAN_OPERATION: "⊕",
  LINE: "─",
  SECTION: "§",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseFigmaUrl(url) {
  // Handles: figma.com/file/KEY/Name, figma.com/design/KEY/Name
  const match = url.match(/figma\.com\/(?:file|design)\/([a-zA-Z0-9]+)/);
  if (!match) return null;
  const fileKey = match[1];

  // Parse optional node-id (formats: node-id=1:2 or node-id=1-2)
  const nodeMatch = url.match(/node-id=([^&]+)/);
  let nodeId = null;
  if (nodeMatch) {
    nodeId = decodeURIComponent(nodeMatch[1]).replace("-", ":");
  }

  return { fileKey, nodeId };
}

function flattenNodes(node, depth = 0) {
  const results = [];

  if (node.absoluteBoundingBox && depth > 0) {
    results.push({
      id: node.id,
      name: node.name,
      type: node.type,
      bbox: node.absoluteBoundingBox,
      depth,
      childCount: node.children?.length || 0,
    });
  }

  if (node.children) {
    for (const child of node.children) {
      results.push(...flattenNodes(child, depth + 1));
    }
  }

  return results;
}

function buildTree(node, depth = 0, maxDepth = 10) {
  if (depth > maxDepth) return null;
  const treeNode = {
    id: node.id,
    name: node.name,
    type: node.type,
    bbox: node.absoluteBoundingBox,
    children: [],
  };
  if (node.children) {
    for (const child of node.children) {
      const childTree = buildTree(child, depth + 1, maxDepth);
      if (childTree) treeNode.children.push(childTree);
    }
  }
  return treeNode;
}

function findSmallestNodeAt(nodes, figmaX, figmaY) {
  let best = null;
  let bestArea = Infinity;

  for (const node of nodes) {
    const { x, y, width, height } = node.bbox;
    if (
      figmaX >= x && figmaX <= x + width &&
      figmaY >= y && figmaY <= y + height
    ) {
      const area = width * height;
      if (area < bestArea) {
        best = node;
        bestArea = area;
      }
    }
  }
  return best;
}

function getNodeRelativePosition(nodeBBox, frameBBox) {
  const centerX = nodeBBox.x + nodeBBox.width / 2;
  const centerY = nodeBBox.y + nodeBBox.height / 2;
  return {
    x: ((centerX - frameBBox.x) / frameBBox.width) * 100,
    y: ((centerY - frameBBox.y) / frameBBox.height) * 100,
    left: ((nodeBBox.x - frameBBox.x) / frameBBox.width) * 100,
    top: ((nodeBBox.y - frameBBox.y) / frameBBox.height) * 100,
    width: (nodeBBox.width / frameBBox.width) * 100,
    height: (nodeBBox.height / frameBBox.height) * 100,
  };
}

/**
 * Calls the backend Figma proxy endpoints.
 * Maps the original POC "endpoint" style to the real proxy routes:
 *   files/{key}?depth=2         → GET /api/figma/proxy/files/{key}
 *   files/{key}/nodes?ids=...   → GET /api/figma/proxy/files/{key}/nodes?ids=...
 *   images/{key}?ids=...&...    → GET /api/figma/proxy/image-urls/{key}?ids=...&...
 */
async function figmaFetch(token, endpoint, retries = 3) {
  // Convert POC-style endpoint to real proxy URL
  let url;
  if (endpoint.startsWith("files/")) {
    const nodesMatch = endpoint.match(/^files\/([^/]+)\/nodes\?(.+)$/);
    const fileMatch = endpoint.match(/^files\/([^/?]+)(\?.*)?$/);
    if (nodesMatch) {
      url = `/api/figma/proxy/files/${nodesMatch[1]}/nodes?${nodesMatch[2]}`;
    } else if (fileMatch) {
      url = `/api/figma/proxy/files/${fileMatch[1]}${fileMatch[2] || ''}`;
    }
  } else if (endpoint.startsWith("images/")) {
    const imgMatch = endpoint.match(/^images\/([^?]+)\?(.+)$/);
    if (imgMatch) {
      url = `/api/figma/proxy/image-urls/${imgMatch[1]}?${imgMatch[2]}`;
    }
  }

  if (!url) {
    throw new Error(`Unknown Figma endpoint: ${endpoint}`);
  }

  for (let attempt = 0; attempt <= retries; attempt++) {
    const res = await fetch(url);

    if (res.status === 429 && attempt < retries) {
      const retryAfter = res.headers.get("Retry-After");
      let backoff;
      if (retryAfter) {
        const parsed = parseInt(retryAfter, 10);
        // Figma sends Retry-After in ms; standard HTTP uses seconds
        backoff = parsed > 1000 ? parsed : parsed * 1000;
      } else {
        backoff = Math.pow(2, attempt) * 15000; // 15s, 30s, 60s
      }
      console.warn(`[figmaFetch] 429 Rate limited. Retry-After: ${retryAfter ?? "not set"}. Waiting ${(backoff / 1000).toFixed(1)}s (attempt ${attempt + 1}/${retries})`);
      await new Promise((resolve) => setTimeout(resolve, backoff));
      continue;
    }

    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data;
  }
}

function storageKey(fileKey, frameId) {
  return `figma-annotations-${fileKey}-${frameId}`;
}

function loadAnnotations(fileKey, frameId) {
  try {
    const raw = localStorage.getItem(storageKey(fileKey, frameId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveAnnotations(fileKey, frameId, annotations) {
  try {
    localStorage.setItem(storageKey(fileKey, frameId), JSON.stringify(annotations));
  } catch { /* ignore */ }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const PinIcon = React.memo(function PinIcon({ number, color, size = 32, active = false }) {
  return (
    <svg width={size} height={size + 8} viewBox="0 0 32 40" style={{ filter: active ? "drop-shadow(0 0 8px rgba(0,0,0,0.3))" : "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }}>
      <path
        d="M16 0C7.16 0 0 7.16 0 16c0 12 16 24 16 24s16-12 16-24C32 7.16 24.84 0 16 0z"
        fill={color} stroke="white" strokeWidth="2"
      />
      <circle cx="16" cy="15" r="10" fill="white" opacity="0.95" />
      <text x="16" y="19" textAnchor="middle" fontSize="11" fontWeight="700" fill={color}>
        {number}
      </text>
    </svg>
  );
});

const NodeTreeItem = React.memo(function NodeTreeItem({ node, depth, selectedNodeId, hoveredNodeId, annotationCountByNode, onSelect, onHover, expandedIds, onToggle }) {
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedNodeId === node.id;
  const isHovered = hoveredNodeId === node.id;
  const annCount = annotationCountByNode[node.id] || 0;
  const icon = NODE_TYPE_ICONS[node.type] || "•";
  const expanded = expandedIds.has(node.id);

  return (
    <>
      <div
        onClick={() => onSelect(node)}
        onMouseEnter={() => onHover(node.id)}
        onMouseLeave={() => onHover(null)}
        style={{
          display: "flex", alignItems: "center", gap: 4,
          padding: "4px 8px", paddingLeft: 8 + depth * 16,
          cursor: "pointer", fontSize: 12,
          background: isSelected ? "#EFF6FF" : isHovered ? "#F8FAFC" : "transparent",
          borderLeft: isSelected ? "3px solid #3B82F6" : "3px solid transparent",
          transition: "all 0.1s",
        }}
      >
        {hasChildren ? (
          <span
            onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
            style={{ width: 16, textAlign: "center", fontSize: 10, color: "#94A3B8", cursor: "pointer", flexShrink: 0 }}
          >
            {expanded ? "▼" : "▶"}
          </span>
        ) : (
          <span style={{ width: 16, flexShrink: 0 }} />
        )}
        <span style={{ fontSize: 11, opacity: 0.6, flexShrink: 0, width: 16, textAlign: "center" }}>{icon}</span>
        <span style={{
          flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          fontWeight: isSelected ? 600 : 400,
          color: isSelected ? "#2563EB" : "#334155",
        }}>
          {node.name}
        </span>
        {annCount > 0 && (
          <span style={{
            background: "#3B82F6", color: "white", borderRadius: 10,
            padding: "1px 6px", fontSize: 10, fontWeight: 700, flexShrink: 0,
          }}>
            {annCount}
          </span>
        )}
      </div>
      {expanded && hasChildren && node.children.map((child) => (
        <NodeTreeItem
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedNodeId={selectedNodeId}
          hoveredNodeId={hoveredNodeId}
          annotationCountByNode={annotationCountByNode}
          onSelect={onSelect}
          onHover={onHover}
          expandedIds={expandedIds}
          onToggle={onToggle}
        />
      ))}
    </>
  );
});

function NodeTree({ tree, selectedNodeId, hoveredNodeId, annotationCountByNode, onSelectNode, onHoverNode }) {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [search, setSearch] = useState("");

  // Auto-expand first level on tree load
  useEffect(() => {
    if (tree?.children) {
      setExpandedIds(new Set([tree.id, ...tree.children.map((c) => c.id)]));
    }
  }, [tree]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Filter nodes by search
  const filterTree = useCallback((node) => {
    if (!search.trim()) return node;
    const matches = node.name.toLowerCase().includes(search.toLowerCase());
    const filteredChildren = node.children
      ?.map(filterTree)
      .filter(Boolean) || [];
    if (matches || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
    return null;
  }, [search]);

  const filteredTree = tree ? filterTree(tree) : null;

  // When searching, build an expandedIds set that includes all filtered node IDs
  const effectiveExpandedIds = useMemo(() => {
    if (!search.trim()) return expandedIds;
    // Expand everything when searching
    const allIds = new Set();
    const collectIds = (node) => {
      if (!node) return;
      allIds.add(node.id);
      node.children?.forEach(collectIds);
    };
    collectIds(filteredTree);
    return allIds;
  }, [search, expandedIds, filteredTree]);

  if (!tree) {
    return (
      <div style={{ padding: 20, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
        Load a Figma file to see components
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: 8 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search components..."
          style={{
            width: "100%", padding: "6px 10px", borderRadius: 8,
            border: "1px solid #E2E8F0", fontSize: 12, outline: "none",
            boxSizing: "border-box",
          }}
          onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
          onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
        />
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {filteredTree?.children?.map((child) => (
          <NodeTreeItem
            key={child.id}
            node={child}
            depth={0}
            selectedNodeId={selectedNodeId}
            hoveredNodeId={hoveredNodeId}
            annotationCountByNode={annotationCountByNode}
            onSelect={onSelectNode}
            onHover={onHoverNode}
            expandedIds={effectiveExpandedIds}
            onToggle={toggleExpand}
          />
        ))}
      </div>
    </div>
  );
}

function AnnotationForm({ node, onSave, onCancel }) {
  const [title, setTitle] = useState(node ? node.name : "");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("action");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = () => {
    if (!title.trim()) return;
    onSave({ title: title.trim(), description: description.trim(), category });
  };

  return (
    <div style={{
      background: "white", borderRadius: 16, padding: 20, width: 340,
      boxShadow: "0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)",
      animation: "fadeIn 0.15s ease-out",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>New Annotation</span>
        <button onClick={onCancel} style={{
          background: "none", border: "none", cursor: "pointer", fontSize: 18,
          color: "#94A3B8", padding: "2px 6px", borderRadius: 6,
        }}>×</button>
      </div>

      {node && (
        <div style={{
          display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
          background: "#F0F9FF", borderRadius: 8, marginBottom: 12,
          border: "1px solid #BAE6FD", fontSize: 12, color: "#0369A1",
        }}>
          <span style={{ fontSize: 11, opacity: 0.7 }}>{NODE_TYPE_ICONS[node.type] || "•"}</span>
          <span style={{ fontWeight: 600 }}>Linked to:</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
        </div>
      )}

      <input
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        placeholder="Annotation title..."
        style={{
          width: "100%", padding: "10px 12px", border: "2px solid #E2E8F0",
          borderRadius: 10, fontSize: 14, outline: "none", marginBottom: 12,
          boxSizing: "border-box", transition: "border 0.15s",
        }}
        onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
        onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
      />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setCategory(cat.id)}
            style={{
              padding: "5px 10px", borderRadius: 8, fontSize: 12, cursor: "pointer",
              border: category === cat.id ? "2px solid #3B82F6" : "2px solid #E2E8F0",
              background: category === cat.id ? "#EFF6FF" : "white",
              color: category === cat.id ? "#2563EB" : "#64748B",
              fontWeight: category === cat.id ? 600 : 400,
              transition: "all 0.15s",
            }}
          >
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Describe behavior, API calls, state changes..."
        rows={4}
        style={{
          width: "100%", padding: "10px 12px", border: "2px solid #E2E8F0",
          borderRadius: 10, fontSize: 13, outline: "none", resize: "vertical",
          fontFamily: "inherit", marginBottom: 14, boxSizing: "border-box",
          lineHeight: 1.5,
        }}
        onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
        onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
      />

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{
          padding: "8px 16px", borderRadius: 10, border: "1px solid #E2E8F0",
          background: "white", cursor: "pointer", fontSize: 13, color: "#64748B", fontWeight: 500,
        }}>Cancel</button>
        <button
          onClick={handleSubmit}
          disabled={!title.trim()}
          style={{
            padding: "8px 20px", borderRadius: 10, border: "none",
            background: title.trim() ? "#3B82F6" : "#CBD5E1",
            color: "white", cursor: title.trim() ? "pointer" : "default",
            fontSize: 13, fontWeight: 600, transition: "background 0.15s",
          }}
        >
          Save Pin
        </button>
      </div>
    </div>
  );
}

function AnnotationDetail({ annotation, index, nodePosition, onClose, onDelete }) {
  const cat = CATEGORIES.find((c) => c.id === annotation.category) || CATEGORIES[5];
  const color = COLORS[index % COLORS.length];

  return (
    <div style={{
      background: "white", borderRadius: 16, width: 320,
      boxShadow: "0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)",
      overflow: "hidden", animation: "fadeIn 0.15s ease-out",
    }}>
      <div style={{
        background: `linear-gradient(135deg, ${color}, ${color}dd)`,
        padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            background: "rgba(255,255,255,0.25)", borderRadius: 8,
            padding: "2px 8px", fontSize: 12, color: "white", fontWeight: 700,
          }}>#{index + 1}</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: "white" }}>{annotation.title}</span>
        </div>
        <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer",
          fontSize: 16, color: "white", borderRadius: 6, padding: "2px 8px",
        }}>×</button>
      </div>

      <div style={{ padding: 16 }}>
        {/* Linked component */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6, padding: "5px 10px",
          background: "#F0F9FF", borderRadius: 6, marginBottom: 10,
          fontSize: 11, color: "#0369A1", fontWeight: 500,
        }}>
          <span>{NODE_TYPE_ICONS[annotation.nodeType] || "•"}</span>
          <span>Linked to: <strong>{annotation.nodeName}</strong></span>
        </div>

        <div style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          background: "#F1F5F9", borderRadius: 6, padding: "4px 10px",
          fontSize: 12, color: "#475569", marginBottom: 10, fontWeight: 500,
        }}>
          {cat.icon} {cat.label}
        </div>

        {annotation.description && (
          <p style={{
            fontSize: 13, color: "#334155", lineHeight: 1.65,
            margin: "8px 0 12px", whiteSpace: "pre-wrap",
          }}>
            {annotation.description}
          </p>
        )}

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          paddingTop: 10, borderTop: "1px solid #F1F5F9",
        }}>
          <span style={{ fontSize: 11, color: "#94A3B8" }}>
            {annotation.author || "You"} · {new Date(annotation.timestamp).toLocaleString()}
          </span>
          <button
            onClick={() => onDelete(annotation.id)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 11, color: "#EF4444", fontWeight: 500, padding: "4px 8px",
              borderRadius: 6,
            }}
            onMouseEnter={(e) => e.target.style.background = "#FEF2F2"}
            onMouseLeave={(e) => e.target.style.background = "none"}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function SidePanel({ tree, flatNodes, annotations, activeAnnotation, selectedNodeId, hoveredNodeId, onSelectNode, onHoverNode, onSelectAnnotation, onExport, sidebarTab, onTabChange }) {
  const annotationCountByNode = useMemo(() => {
    const counts = {};
    for (const ann of annotations) {
      counts[ann.nodeId] = (counts[ann.nodeId] || 0) + 1;
    }
    return counts;
  }, [annotations]);

  return (
    <div style={{
      width: 320, background: "white", borderLeft: "1px solid #E2E8F0",
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      {/* Tab Header */}
      <div style={{
        display: "flex", borderBottom: "1px solid #E2E8F0",
      }}>
        <button
          onClick={() => onTabChange("tree")}
          style={{
            flex: 1, padding: "12px 8px", border: "none", cursor: "pointer",
            fontSize: 12, fontWeight: 600,
            background: sidebarTab === "tree" ? "white" : "#F8FAFC",
            color: sidebarTab === "tree" ? "#3B82F6" : "#64748B",
            borderBottom: sidebarTab === "tree" ? "2px solid #3B82F6" : "2px solid transparent",
          }}
        >
          🖼 Components
        </button>
        <button
          onClick={() => onTabChange("annotations")}
          style={{
            flex: 1, padding: "12px 8px", border: "none", cursor: "pointer",
            fontSize: 12, fontWeight: 600,
            background: sidebarTab === "annotations" ? "white" : "#F8FAFC",
            color: sidebarTab === "annotations" ? "#3B82F6" : "#64748B",
            borderBottom: sidebarTab === "annotations" ? "2px solid #3B82F6" : "2px solid transparent",
          }}
        >
          📌 Annotations ({annotations.length})
        </button>
      </div>

      {/* Tab Content */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {sidebarTab === "tree" ? (
          <NodeTree
            tree={tree}
            selectedNodeId={selectedNodeId}
            hoveredNodeId={hoveredNodeId}
            annotationCountByNode={annotationCountByNode}
            onSelectNode={onSelectNode}
            onHoverNode={onHoverNode}
          />
        ) : (
          <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
            {annotations.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#94A3B8" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📌</div>
                <div style={{ fontSize: 13 }}>Select a component from the tree to add annotations</div>
              </div>
            )}

            {/* Group annotations by node */}
            {(() => {
              const groups = {};
              annotations.forEach((ann, i) => {
                if (!groups[ann.nodeId]) {
                  groups[ann.nodeId] = { nodeName: ann.nodeName, nodeType: ann.nodeType, items: [] };
                }
                groups[ann.nodeId].items.push({ ann, globalIndex: i });
              });

              return Object.entries(groups).map(([nodeId, group]) => (
                <div key={nodeId} style={{ marginBottom: 12 }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
                    fontSize: 11, color: "#64748B", fontWeight: 600,
                  }}>
                    <span>{NODE_TYPE_ICONS[group.nodeType] || "•"}</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {group.nodeName}
                    </span>
                  </div>
                  {group.items.map(({ ann, globalIndex }) => {
                    const cat = CATEGORIES.find((c) => c.id === ann.category) || CATEGORIES[5];
                    const color = COLORS[globalIndex % COLORS.length];
                    const isActive = activeAnnotation === ann.id;
                    return (
                      <button
                        key={ann.id}
                        onClick={() => onSelectAnnotation(ann.id)}
                        style={{
                          width: "100%", textAlign: "left", padding: "10px 12px",
                          borderRadius: 10,
                          border: isActive ? `2px solid ${color}` : "2px solid transparent",
                          background: isActive ? `${color}08` : "transparent",
                          cursor: "pointer", marginBottom: 2, transition: "all 0.15s",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{
                            background: color, color: "white", borderRadius: 6,
                            width: 22, height: 22, display: "flex", alignItems: "center",
                            justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0,
                          }}>{globalIndex + 1}</span>
                          <div style={{ overflow: "hidden" }}>
                            <div style={{
                              fontSize: 13, fontWeight: 600, color: "#111",
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            }}>{ann.title}</div>
                            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>
                              {cat.icon} {cat.label}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ));
            })()}

            {annotations.length > 0 && (
              <div style={{ padding: "8px 10px", borderTop: "1px solid #E2E8F0", marginTop: 8 }}>
                <button onClick={onExport} style={{
                  width: "100%", padding: "8px 12px", borderRadius: 8,
                  border: "1px solid #E2E8F0", background: "white",
                  cursor: "pointer", fontSize: 12, color: "#475569", fontWeight: 500,
                }}>
                  📋 Export All
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FigmaAnnotatorOriginal() {
  // Figma connection state
  const [figmaUrl, setFigmaUrl] = useState("");
  const [figmaToken, setFigmaToken] = useState("");
  const [fileKey, setFileKey] = useState(null);
  const [fileName, setFileName] = useState("");
  const [pages, setPages] = useState([]);
  const [selectedFrameId, setSelectedFrameId] = useState(null);
  const [selectedFrameName, setSelectedFrameName] = useState("");
  const [frameTree, setFrameTree] = useState(null);
  const [flatNodes, setFlatNodes] = useState([]);
  const [frameBBox, setFrameBBox] = useState(null);
  const [frameImage, setFrameImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Annotation state
  const [annotations, setAnnotations] = useState([]);
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null); // node selected for new annotation
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [mode, setMode] = useState("view");
  const [showExport, setShowExport] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [sidebarTab, setSidebarTab] = useState("tree");
  const [imageLoading, setImageLoading] = useState(false);

  // Zoom & pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panStartOffset = useRef({ x: 0, y: 0 });
  const spacePressed = useRef(false);

  const canvasRef = useRef(null);
  const imageContainerRef = useRef(null);
  const imageUrlCache = useRef(new Map());

  // O(1) node lookup map
  const nodeMap = useMemo(() => {
    const map = new Map();
    for (const node of flatNodes) {
      map.set(node.id, node);
    }
    return map;
  }, [flatNodes]);

  // Load token from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("figma-token");
    if (saved) setFigmaToken(saved);
  }, []);

  // Persist token
  useEffect(() => {
    if (figmaToken) localStorage.setItem("figma-token", figmaToken);
  }, [figmaToken]);

  // Persist annotations
  useEffect(() => {
    if (fileKey && selectedFrameId) {
      saveAnnotations(fileKey, selectedFrameId, annotations);
    }
  }, [annotations, fileKey, selectedFrameId]);

  // Reset zoom/pan when frame changes
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [selectedFrameId]);

  // ─── Zoom & Pan Handlers ──────────────────────────────────────────

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const container = canvasRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;

    const zoomFactor = e.deltaY < 0 ? 1.1 : 1 / 1.1;

    setZoom((prevZoom) => {
      const newZoom = Math.min(4, Math.max(0.25, prevZoom * zoomFactor));
      const scale = newZoom / prevZoom;

      setPan((prevPan) => ({
        x: cursorX - scale * (cursorX - prevPan.x),
        y: cursorY - scale * (cursorY - prevPan.y),
      }));

      return newZoom;
    });
  }, []);

  // Attach wheel listener with { passive: false } to allow preventDefault
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const handlePointerDown = useCallback((e) => {
    // Middle-click or Space+left-click to pan
    if (e.button === 1 || (e.button === 0 && spacePressed.current)) {
      e.preventDefault();
      isPanning.current = true;
      panStart.current = { x: e.clientX, y: e.clientY };
      panStartOffset.current = { ...pan };
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  }, [pan]);

  const handlePointerMove = useCallback((e) => {
    if (!isPanning.current) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    setPan({
      x: panStartOffset.current.x + dx,
      y: panStartOffset.current.y + dy,
    });
  }, []);

  const handlePointerUp = useCallback((e) => {
    if (isPanning.current) {
      isPanning.current = false;
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  // Track Space key for space+drag panning
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === "Space" && !e.repeat) {
        spacePressed.current = true;
      }
    };
    const handleKeyUp = (e) => {
      if (e.code === "Space") {
        spacePressed.current = false;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(4, z * 1.25));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(0.25, z / 1.25));
  }, []);

  const handleZoomFit = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // ─── Load Figma File ────────────────────────────────────────────────

  const handleLoad = async () => {
    const parsed = parseFigmaUrl(figmaUrl);
    if (!parsed) {
      setError("Invalid Figma URL. Use a figma.com/file/... or figma.com/design/... URL.");
      return;
    }
    if (!figmaToken.trim()) {
      setError("Please enter your Figma Personal Access Token.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // Fetch file structure (depth=2 to get pages + top-level frames)
      const fileData = await figmaFetch(figmaToken, `files/${parsed.fileKey}?depth=2`);
      setFileKey(parsed.fileKey);
      setFileName(fileData.name);

      // Extract pages and their frames
      const pageList = fileData.document.children.map((page) => ({
        id: page.id,
        name: page.name,
        frames: (page.children || [])
          .filter((f) => f.type === "FRAME" || f.type === "SECTION" || f.type === "COMPONENT")
          .map((f) => ({ id: f.id, name: f.name, type: f.type })),
      }));
      setPages(pageList);

      // Auto-select frame from URL node-id, or first frame
      let targetFrameId = parsed.nodeId;
      if (!targetFrameId && pageList.length > 0 && pageList[0].frames.length > 0) {
        targetFrameId = pageList[0].frames[0].id;
      }

      if (targetFrameId) {
        await loadFrame(parsed.fileKey, targetFrameId);
      }
    } catch (err) {
      setError(`Failed to load file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ─── Load a Specific Frame ──────────────────────────────────────────

  const loadFrame = async (fKey, frameId) => {
    setLoading(true);
    setError(null);

    try {
      // Fetch the full subtree of this frame
      const nodeData = await figmaFetch(figmaToken, `files/${fKey}/nodes?ids=${encodeURIComponent(frameId)}`);
      const frameDoc = nodeData.nodes[frameId]?.document;

      if (!frameDoc) {
        throw new Error("Frame not found");
      }

      setSelectedFrameId(frameId);
      setSelectedFrameName(frameDoc.name);
      setFrameBBox(frameDoc.absoluteBoundingBox);

      // Build tree and flat list
      const tree = buildTree(frameDoc);
      setFrameTree(tree);
      const flat = flattenNodes(frameDoc);
      setFlatNodes(flat);

      // Fetch rendered image (use cache if available)
      const cacheKey = `${fKey}-${frameId}`;
      let imageUrl = imageUrlCache.current.get(cacheKey);
      if (!imageUrl) {
        const imageData = await figmaFetch(figmaToken, `images/${fKey}?ids=${encodeURIComponent(frameId)}&format=png&scale=1`);
        imageUrl = imageData.images?.[frameId] || null;
        if (imageUrl) imageUrlCache.current.set(cacheKey, imageUrl);
      }
      setFrameImage(imageUrl);
      if (imageUrl) setImageLoading(true);

      // Load saved annotations for this frame
      const saved = loadAnnotations(fKey, frameId);
      setAnnotations(saved);
      setActiveAnnotation(null);
      setSelectedNode(null);
      setShowForm(false);
    } catch (err) {
      setError(`Failed to load frame: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ─── Refresh (re-fetch positions) ───────────────────────────────────

  const handleRefresh = async () => {
    if (!fileKey || !selectedFrameId) return;
    setLoading(true);
    setError(null);

    try {
      const nodeData = await figmaFetch(figmaToken, `files/${fileKey}/nodes?ids=${encodeURIComponent(selectedFrameId)}`);
      const frameDoc = nodeData.nodes[selectedFrameId]?.document;

      if (!frameDoc) throw new Error("Frame not found");

      setFrameBBox(frameDoc.absoluteBoundingBox);
      const tree = buildTree(frameDoc);
      setFrameTree(tree);
      const flat = flattenNodes(frameDoc);
      setFlatNodes(flat);

      // Re-fetch image (invalidate cache on refresh)
      const imageData = await figmaFetch(figmaToken, `images/${fileKey}?ids=${encodeURIComponent(selectedFrameId)}&format=png&scale=1`);
      const refreshedUrl = imageData.images?.[selectedFrameId] || null;
      const cacheKey = `${fileKey}-${selectedFrameId}`;
      if (refreshedUrl) imageUrlCache.current.set(cacheKey, refreshedUrl);
      setFrameImage(refreshedUrl);
      if (refreshedUrl) setImageLoading(true);

      // Update annotation node names (in case they were renamed)
      const flatMap = new Map();
      for (const n of flat) flatMap.set(n.id, n);
      setAnnotations((prev) =>
        prev.map((ann) => {
          const node = flatMap.get(ann.nodeId);
          if (node) {
            return { ...ann, nodeName: node.name, nodeType: node.type };
          }
          return { ...ann, _orphaned: true };
        })
      );
    } catch (err) {
      setError(`Refresh failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ─── Canvas Click → Detect Component ────────────────────────────────

  const handleCanvasClick = useCallback((e) => {
    if (mode !== "annotate" || !frameBBox || flatNodes.length === 0) return;
    if (spacePressed.current) return; // Ignore clicks during space-pan

    const container = imageContainerRef.current;
    if (!container) return;

    const img = container.querySelector("img");
    if (!img) return;

    const imgRect = img.getBoundingClientRect();
    const clickX = e.clientX - imgRect.left;
    const clickY = e.clientY - imgRect.top;

    // Convert to figma coordinates
    const figmaX = frameBBox.x + (clickX / imgRect.width) * frameBBox.width;
    const figmaY = frameBBox.y + (clickY / imgRect.height) * frameBBox.height;

    const node = findSmallestNodeAt(flatNodes, figmaX, figmaY);
    if (node) {
      setSelectedNode(node);
      setShowForm(true);
      setActiveAnnotation(null);
      setSidebarTab("tree");
    }
  }, [mode, frameBBox, flatNodes]);

  // ─── Node selection from tree ───────────────────────────────────────

  const handleSelectNodeFromTree = useCallback((treeNode) => {
    const node = nodeMap.get(treeNode.id) || {
      id: treeNode.id,
      name: treeNode.name,
      type: treeNode.type,
      bbox: treeNode.bbox,
    };

    if (mode === "annotate") {
      setSelectedNode(node);
      setShowForm(true);
      setActiveAnnotation(null);
    } else {
      // In view mode, just highlight the node
      setSelectedNode(node);
      setActiveAnnotation(null);
    }
  }, [mode, nodeMap]);

  // ─── Get pin position ───────────────────────────────────────────────

  const getPosition = useCallback((nodeId) => {
    if (!frameBBox) return null;
    const node = nodeMap.get(nodeId);
    if (!node?.bbox) return null;
    return getNodeRelativePosition(node.bbox, frameBBox);
  }, [nodeMap, frameBBox]);

  // ─── Save Annotation ────────────────────────────────────────────────

  const handleSaveAnnotation = (data) => {
    if (!selectedNode) return;

    const ann = {
      id: `ann-${Date.now()}`,
      nodeId: selectedNode.id,
      nodeName: selectedNode.name,
      nodeType: selectedNode.type,
      ...data,
      author: "You",
      timestamp: Date.now(),
    };

    setAnnotations((prev) => [...prev, ann]);
    setShowForm(false);
    setSelectedNode(null);
    setActiveAnnotation(ann.id);
    setSidebarTab("annotations");
  };

  // ─── Delete ─────────────────────────────────────────────────────────

  const handleDeleteAnnotation = (id) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    setActiveAnnotation(null);
  };

  // ─── Export ─────────────────────────────────────────────────────────

  const handleExport = () => {
    const groups = {};
    annotations.forEach((ann, i) => {
      if (!groups[ann.nodeId]) {
        groups[ann.nodeId] = { nodeName: ann.nodeName, items: [] };
      }
      groups[ann.nodeId].items.push({ ann, index: i });
    });

    let md = `# Figma Annotations Export\n**File:** ${fileName}\n**Frame:** ${selectedFrameName}\n**Exported:** ${new Date().toLocaleString()}\n\n`;

    for (const [nodeId, group] of Object.entries(groups)) {
      md += `## Component: ${group.nodeName}\n`;
      md += `Node ID: \`${nodeId}\`\n\n`;
      for (const { ann, index } of group.items) {
        const cat = CATEGORIES.find((c) => c.id === ann.category) || CATEGORIES[5];
        md += `### ${index + 1}. ${ann.title}\n`;
        md += `- **Category:** ${cat.icon} ${cat.label}\n`;
        md += `- **Description:** ${ann.description || "N/A"}\n`;
        md += `- **Author:** ${ann.author} | ${new Date(ann.timestamp).toLocaleString()}\n\n`;
      }
    }
    setShowExport(md);
  };

  // ─── Frame options ──────────────────────────────────────────────────

  const allFrames = useMemo(() => {
    const frames = [];
    for (const page of pages) {
      for (const frame of page.frames) {
        frames.push({ ...frame, pageName: page.name });
      }
    }
    return frames;
  }, [pages]);

  // ─── Hovered node highlight position ────────────────────────────────

  const highlightNodeId = hoveredNodeId || selectedNode?.id || null;
  const highlightPos = highlightNodeId ? getPosition(highlightNodeId) : null;

  // ─── Render ─────────────────────────────────────────────────────────

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#F8FAFC", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.15); } }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
      `}</style>

      {/* ─── Header ─────────────────────────────────────────────── */}
      <div style={{
        padding: "8px 16px", background: "white", borderBottom: "1px solid #E2E8F0",
        display: "flex", alignItems: "center", gap: 12, zIndex: 100, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            background: "linear-gradient(135deg, #3B82F6, #8B5CF6)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
          }}>📐</div>
          <span style={{ fontWeight: 800, fontSize: 15, color: "#111" }}>Figma Annotator</span>
          <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 500 }}>(Original POC)</span>
        </div>

        <input
          value={figmaToken}
          onChange={(e) => setFigmaToken(e.target.value)}
          type="password"
          placeholder="Figma token..."
          style={{
            width: 140, padding: "7px 10px", borderRadius: 8,
            border: "1px solid #E2E8F0", fontSize: 12, outline: "none",
          }}
          onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
          onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
        />

        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 200, maxWidth: 460 }}>
          <input
            value={figmaUrl}
            onChange={(e) => setFigmaUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLoad()}
            placeholder="Paste Figma file URL..."
            style={{
              flex: 1, padding: "7px 10px", borderRadius: 8,
              border: "1px solid #E2E8F0", fontSize: 12, outline: "none",
            }}
            onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
            onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
          />
          <button onClick={handleLoad} disabled={loading} style={{
            padding: "7px 16px", borderRadius: 8, border: "none",
            background: "#3B82F6", color: "white", fontSize: 12,
            fontWeight: 600, cursor: loading ? "wait" : "pointer", whiteSpace: "nowrap",
            opacity: loading ? 0.7 : 1,
          }}>
            {loading ? "Loading..." : "Load"}
          </button>
        </div>

        {/* Frame Selector */}
        {allFrames.length > 0 && (
          <select
            value={selectedFrameId || ""}
            onChange={(e) => loadFrame(fileKey, e.target.value)}
            style={{
              padding: "7px 10px", borderRadius: 8, border: "1px solid #E2E8F0",
              fontSize: 12, outline: "none", maxWidth: 200, cursor: "pointer",
            }}
          >
            {allFrames.map((f) => (
              <option key={f.id} value={f.id}>
                {f.pageName} / {f.name}
              </option>
            ))}
          </select>
        )}

        {/* Mode Toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#F1F5F9", borderRadius: 8, padding: 3, flexShrink: 0 }}>
          <button
            onClick={() => { setMode("view"); setShowForm(false); }}
            style={{
              padding: "5px 12px", borderRadius: 6, border: "none",
              background: mode === "view" ? "white" : "transparent",
              boxShadow: mode === "view" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              color: mode === "view" ? "#111" : "#64748B",
            }}
          >
            👁 View
          </button>
          <button
            onClick={() => setMode("annotate")}
            style={{
              padding: "5px 12px", borderRadius: 6, border: "none",
              background: mode === "annotate" ? "white" : "transparent",
              boxShadow: mode === "annotate" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              color: mode === "annotate" ? "#111" : "#64748B",
            }}
          >
            📌 Annotate
          </button>
        </div>

        {/* Refresh */}
        {fileKey && (
          <button onClick={handleRefresh} disabled={loading} style={{
            padding: "5px 10px", borderRadius: 8, border: "1px solid #E2E8F0",
            background: "white", cursor: loading ? "wait" : "pointer",
            fontSize: 12, fontWeight: 500, color: "#475569", flexShrink: 0,
          }}>
            🔄 Refresh
          </button>
        )}
      </div>

      {/* ─── Error Bar ──────────────────────────────────────────── */}
      {error && (
        <div style={{
          padding: "8px 16px", background: "#FEF2F2", borderBottom: "1px solid #FECACA",
          color: "#DC2626", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{
            background: "none", border: "none", cursor: "pointer", color: "#DC2626", fontSize: 16,
          }}>×</button>
        </div>
      )}

      {/* ─── Main Content ───────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Canvas Area */}
        <div
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{
            flex: 1, position: "relative", overflow: "hidden",
            cursor: spacePressed.current || isPanning.current ? "grabbing" : "default",
          }}
        >
          {/* Annotate mode banner */}
          {mode === "annotate" && frameImage && (
            <div style={{
              position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
              background: "#111", color: "white", padding: "8px 16px", borderRadius: 10,
              fontSize: 12, fontWeight: 600, zIndex: 50, display: "inline-flex", alignItems: "center", gap: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)", width: "fit-content", pointerEvents: "none",
            }}>
              <span style={{ animation: "pulse 2s infinite", display: "inline-block" }}>📌</span>
              Click a component on the canvas or in the tree to annotate
            </div>
          )}

          {/* Zoom Controls */}
          {frameImage && (
            <div style={{
              position: "absolute", bottom: 16, left: 16, zIndex: 50,
              display: "flex", alignItems: "center", gap: 4,
              background: "white", borderRadius: 10, padding: 4,
              boxShadow: "0 2px 12px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)",
            }}>
              <button onClick={handleZoomOut} style={{
                width: 32, height: 32, borderRadius: 8, border: "none",
                background: "transparent", cursor: "pointer", fontSize: 16,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#475569",
              }}>−</button>
              <span style={{
                fontSize: 12, fontWeight: 600, color: "#334155",
                minWidth: 44, textAlign: "center", userSelect: "none",
              }}>{Math.round(zoom * 100)}%</span>
              <button onClick={handleZoomIn} style={{
                width: 32, height: 32, borderRadius: 8, border: "none",
                background: "transparent", cursor: "pointer", fontSize: 16,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#475569",
              }}>+</button>
              <div style={{ width: 1, height: 20, background: "#E2E8F0" }} />
              <button onClick={handleZoomFit} style={{
                height: 32, borderRadius: 8, border: "none",
                background: "transparent", cursor: "pointer", fontSize: 12,
                fontWeight: 500, color: "#475569", padding: "0 8px",
              }}>Fit</button>
            </div>
          )}

          {frameImage ? (
            <div
              style={{
                transformOrigin: "0 0",
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                willChange: "transform",
              }}
            >
              <div
                ref={imageContainerRef}
                onClick={handleCanvasClick}
                style={{
                  position: "relative",
                  display: "inline-block",
                  cursor: mode === "annotate" ? "crosshair" : "default",
                  lineHeight: 0,
                }}
              >
                {imageLoading && (
                  <div style={{
                    position: "absolute", inset: 0, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    background: "#F1F5F9", zIndex: 1,
                  }}>
                    <div style={{
                      width: 32, height: 32, border: "3px solid #E2E8F0",
                      borderTopColor: "#3B82F6", borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }} />
                  </div>
                )}
                <img
                  src={frameImage}
                  alt={selectedFrameName}
                  style={{ display: "block" }}
                  draggable={false}
                  onLoad={() => setImageLoading(false)}
                />

                {/* Node highlight overlay */}
                {highlightPos && (
                  <div style={{
                    position: "absolute",
                    left: `${highlightPos.left}%`,
                    top: `${highlightPos.top}%`,
                    width: `${highlightPos.width}%`,
                    height: `${highlightPos.height}%`,
                    border: "2px solid #3B82F6",
                    background: "rgba(59, 130, 246, 0.08)",
                    borderRadius: 4,
                    pointerEvents: "none",
                    zIndex: 5,
                    transition: "all 0.15s",
                  }} />
                )}

                {/* Annotation Pins */}
                {annotations.map((ann, i) => {
                  const pos = getPosition(ann.nodeId);
                  if (!pos) return null;
                  const pinScale = 1 / zoom;
                  return (
                    <div
                      key={ann.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveAnnotation(activeAnnotation === ann.id ? null : ann.id);
                        setShowForm(false);
                      }}
                      style={{
                        position: "absolute",
                        left: `${pos.x}%`,
                        top: `${pos.y}%`,
                        transform: `translate(-16px, -36px) scale(${pinScale})`,
                        transformOrigin: "16px 36px",
                        cursor: "pointer",
                        zIndex: activeAnnotation === ann.id ? 900 : 10 + i,
                      }}
                    >
                      <PinIcon
                        number={i + 1}
                        color={COLORS[i % COLORS.length]}
                        active={activeAnnotation === ann.id}
                      />
                    </div>
                  );
                })}

                {/* Active Annotation Detail Popup */}
                {activeAnnotation && (() => {
                  const idx = annotations.findIndex((a) => a.id === activeAnnotation);
                  if (idx === -1) return null;
                  const ann = annotations[idx];
                  const pos = getPosition(ann.nodeId);
                  if (!pos) return null;
                  const popupScale = 1 / zoom;
                  return (
                    <div style={{
                      position: "absolute",
                      left: `${pos.x}%`,
                      top: `${pos.y}%`,
                      transform: pos.x > 60
                        ? `scale(${popupScale}) translate(-100%, -50%)`
                        : `scale(${popupScale}) translate(28px, -50%)`,
                      transformOrigin: pos.x > 60 ? "100% 50%" : "0% 50%",
                      zIndex: 1000,
                    }}>
                      <AnnotationDetail
                        annotation={ann}
                        index={idx}
                        nodePosition={pos}
                        onClose={() => setActiveAnnotation(null)}
                        onDelete={handleDeleteAnnotation}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          ) : (
            /* Empty state / loading */
            <div style={{
              width: "100%", height: "100%", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              background: "linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)",
            }}>
              {loading ? (
                <div style={{ textAlign: "center" }}>
                  <div style={{
                    width: 40, height: 40, border: "4px solid #E2E8F0",
                    borderTopColor: "#3B82F6", borderRadius: "50%",
                    animation: "spin 0.8s linear infinite", margin: "0 auto 16px",
                  }} />
                  <div style={{ fontSize: 14, color: "#64748B" }}>Loading Figma file...</div>
                </div>
              ) : (
                <div style={{
                  background: "white", borderRadius: 20, padding: "40px 50px",
                  textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", maxWidth: 500,
                }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🎨</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 8 }}>
                    Connect to Figma
                  </div>
                  <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.7, marginBottom: 20 }}>
                    Enter your <strong>Figma Personal Access Token</strong> and paste a
                    Figma file URL to get started. Annotations will be linked directly
                    to components — when they move, pins follow.
                  </div>
                  <div style={{
                    background: "#F8FAFC", borderRadius: 12, padding: 16,
                    textAlign: "left", fontSize: 12, color: "#475569", lineHeight: 1.8,
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>How to get a token:</div>
                    <div>1. Go to Figma → Settings → Account</div>
                    <div>2. Scroll to "Personal access tokens"</div>
                    <div>3. Generate a new token and paste it above</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Side Panel */}
        <SidePanel
          tree={frameTree}
          flatNodes={flatNodes}
          annotations={annotations}
          activeAnnotation={activeAnnotation}
          selectedNodeId={selectedNode?.id}
          hoveredNodeId={hoveredNodeId}
          onSelectNode={handleSelectNodeFromTree}
          onHoverNode={setHoveredNodeId}
          onSelectAnnotation={(id) => {
            setActiveAnnotation(activeAnnotation === id ? null : id);
            setShowForm(false);
          }}
          onExport={handleExport}
          sidebarTab={sidebarTab}
          onTabChange={setSidebarTab}
        />
      </div>

      {/* ─── Annotation Form Modal ──────────────────────────────── */}
      {showForm && selectedNode && (
        <div
          onClick={() => { setShowForm(false); setSelectedNode(null); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <AnnotationForm
              node={selectedNode}
              onSave={handleSaveAnnotation}
              onCancel={() => { setShowForm(false); setSelectedNode(null); }}
            />
          </div>
        </div>
      )}

      {/* ─── Export Modal ───────────────────────────────────────── */}
      {showExport && (
        <div
          onClick={() => setShowExport(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white", borderRadius: 20, padding: 24,
              width: 560, maxHeight: "80vh", overflow: "auto",
              boxShadow: "0 24px 80px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>📋 Export Annotations</span>
              <button onClick={() => setShowExport(false)} style={{
                background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#94A3B8",
              }}>×</button>
            </div>
            <pre style={{
              background: "#F8FAFC", padding: 16, borderRadius: 12,
              fontSize: 12, lineHeight: 1.7, whiteSpace: "pre-wrap",
              overflow: "auto", maxHeight: "60vh", border: "1px solid #E2E8F0",
            }}>{showExport}</pre>
            <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
              <button
                onClick={() => navigator.clipboard.writeText(showExport)}
                style={{
                  padding: "10px 20px", borderRadius: 10, border: "none",
                  background: "#3B82F6", color: "white", fontSize: 13,
                  fontWeight: 600, cursor: "pointer",
                }}
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
